import { TerrainGrid, CellState } from './TerrainGrid';
import { EventBus } from '../EventBus';
import { Canonical } from '../hashState';
import { Rng, RngState } from '../Rng';
import { advanceTimer } from '../SimClock';
import { IndustryType, INDUSTRY_CONFIGS, ALL_INDUSTRIES } from './EconomyTypes';
import { EconomyManager } from './EconomyManager';
import { HousingMarket } from './WelfareManager';

// Building footprint shapes (dx, dy offsets from origin)
type FootprintShape = { dx: number; dy: number }[];

type OccupantType = 'family' | 'corporation';

export interface House {
  id: number;
  gridX: number;
  gridY: number;
  footprint: FootprintShape;
  buildProgress: number; // 0-1
  color: string;
  roofColor: string;
  accentColor: string; // For corporate branding
  floors: number; // Height correlates with footprint size
  built: boolean;
  // Occupant info
  occupant: OccupantType;
  familyHome: boolean; // true if family lives here (vs visiting)
  vacant: boolean; // true if family moved out (house available for new family)
  // Economy
  industryType?: IndustryType; // only for corporations
  revenue: number;
  taxesPaid: number;
  // Shareholders (corporations only)
  familyShares: Record<number, number>; // familyIndex -> share count
  corpShares: Record<number, number>;   // ownerCorpHouseId -> share count
  dividendsPaid: number;
}

// Represents a family that can travel between houses
export interface Family {
  id: number;
  name: string; // Family last name (e.g. "Johnson")
  homeHouseId: number;
  currentHouseId: number; // Where they are now
  state: 'home' | 'waiting_for_car' | 'in_car' | 'visiting';
  targetHouseId: number | null; // Where they want to go
  visitTimer: number; // How long they've been visiting
  visitDuration: number; // How long they plan to visit
  cash: number;
  subscriptions: Record<string, number>; // industry -> corpHouseId
  recurringPaymentTimers: Record<string, number>; // industry -> ms since last payment
  shares: Record<number, number>; // corpHouseId -> share count
  /**
   * Consecutive settled budget cycles this household has spent below the line.
   *
   * It counted consecutive cycles with `cash < 0` from the day it was written
   * until ADR-018, and the name is that old rule's. What it counts now is
   * `WelfareManager`'s verdict — a quality-of-life score under
   * `MIGRATE_BELOW`, of which negative cash is one term and still the dominant
   * one. Three of them and the family leaves, which is the number it has always
   * been.
   *
   * The field is not called `strainCycles`, and the reason is not sentiment:
   * this key is in `canonicalState()` and therefore in every golden master in
   * the repository. Renaming it would re-record eleven runs to say that a word
   * changed, which is exactly the re-record nobody can check (ADR-016 D96).
   */
  hardshipCycles: number;
  /**
   * The federal facility they are standing in, or `null` for anywhere else.
   *
   * A family is normally *at a house* — their own or somebody's they are
   * visiting — and `currentHouseId` says which. A shift at a data centre is the
   * first place in this game somebody can be that is not a building anybody
   * lives or trades in, so it needed a second field rather than a made-up house
   * id. `null` for every family that has never had a job, which is every family
   * in a district with no facility on it.
   *
   * Who *employs* them is not here: that is `LabourManager`'s roster, and one
   * copy of it is enough.
   */
  atFacilityId: number | null;
}

export interface ParkingtonNewsItem {
  message: string;
  /**
   * Sim-time in milliseconds, not wall-clock.
   *
   * A headline is part of what the district did, so its stamp has to come from
   * the simulation's own clock. The client is free to render it as "3 minutes
   * ago" against whatever time it likes.
   */
  timestamp: number;
  type: 'move_in' | 'business' | 'economy' | 'switch';
}

/**
 * A family waiting for a lift, and the two ends of the trip.
 *
 * Each end names a building *or* a facility gate and never both: exactly one of
 * `pickupHouseId`/`pickupFacilityId` is non-null, and the same for the
 * destination pair. Two nullable pairs rather than one tagged union because the
 * house ids are what every existing world's traffic is made of — a shape change
 * there would move every golden master recorded before anybody had a job.
 */
export interface CarRequest {
  familyId: number;
  pickupHouseId: number | null;
  destinationHouseId: number | null;
  /** Set when the trip starts at a facility gate: a shift ending. */
  pickupFacilityId?: number;
  /** Set when it ends at one: a shift starting. */
  destinationFacilityId?: number;
}

/**
 * What the district's labour market says, as `HouseManager` needs it.
 *
 * Two questions, asked of `LabourManager` once per family per family-update
 * tick. The argument is omitted entirely while nobody in the district holds a
 * federal job, which is every tick of every district that has never hosted a
 * facility — so a family in an ordinary Parkington makes exactly the decisions,
 * and draws exactly the random numbers, it always did.
 */
export interface CommuteSource {
  /** The facility this family works at, or `null`. */
  jobOf(familyId: number): number | null;
  /** Whether the mayor has called this facility's staff out (§10). */
  isWithdrawn(facilityId: number): boolean;
}

/**
 * The built environment and the people in it, complete.
 * See `HouseManager.save()`.
 */
export interface HouseSnapshot {
  houses: House[];
  families: Family[];
  nextHouseId: number;
  nextFamilyId: number;
  spawnTimer: number;
  familyUpdateTimer: number;
  pendingCarRequests: CarRequest[];
  news: ParkingtonNewsItem[];
  rng: RngState;
}

export interface HouseManagerConfig {
  SPAWN_INTERVAL: number;
  BUILD_SPEED: number;
  FAMILY_UPDATE_INTERVAL: number;
  VISIT_CHANCE: number;
  SUBSCRIPTION_VISIT_CHANCE: number;
  MIN_VISIT_DURATION: number;
  MAX_VISIT_DURATION: number;
  SUBSCRIPTION_CHANCE: number;
  MAX_SUBSCRIPTIONS: number; // kept for settings compatibility, but effectively unused
  SWITCH_CHANCE: number; // chance per family per update tick to re-evaluate one subscription
  /**
   * Chance per update tick that a staff family who is home sets off for work.
   *
   * A rate rather than a rota. §6 says staff *"commute by car every
   * season-day"*, and a season-day here would be a budget cycle — but a shift
   * that started on the cycle boundary would put eight families on the road in
   * the same tick, which is a rush hour the game has no reason to invent.
   *
   * The number buys less than it looks like it should, because the round trip is
   * the bottleneck rather than the decision to make it: a family has to be
   * dispatched a car, driven there, sit a shift, and be driven back. Measured
   * attendance — the fraction of held post-cycles somebody was actually standing
   * in the building — runs 23% at 0.05, 33% here, 39% at 0.20 and 45% at 0.35,
   * so the curve is flat past about a fifth. Since wages are paid against
   * presence (ADR-017), that ceiling is also a ceiling on what a federal job can
   * ever be worth to a household, and it is the honest shape of the thing: in a
   * district with a universal income, a job is a supplement.
   */
  COMMUTE_CHANCE: number;
  /** Shortest shift, in sim-ms. Longer than a visit to the shops, and it should be. */
  MIN_SHIFT_DURATION: number;
  MAX_SHIFT_DURATION: number;
}

export const DEFAULT_HOUSE_CONFIG: HouseManagerConfig = {
  SPAWN_INTERVAL: 2500,
  BUILD_SPEED: 0.0004,
  FAMILY_UPDATE_INTERVAL: 1000,
  VISIT_CHANCE: 0.02,
  SUBSCRIPTION_VISIT_CHANCE: 0.08,
  MIN_VISIT_DURATION: 5000,
  MAX_VISIT_DURATION: 15000,
  SUBSCRIPTION_CHANCE: 0.4,
  MAX_SUBSCRIPTIONS: 6,
  SWITCH_CHANCE: 0.03,
  COMMUTE_CHANCE: 0.12,
  MIN_SHIFT_DURATION: 20000,
  MAX_SHIFT_DURATION: 40000,
};

/**
 * A family's subscribed industries, in a fixed order.
 *
 * `Object.keys(family.subscriptions)` returns whatever order the subscriptions
 * were inserted in, which is deterministic but incidental: it depends on the
 * order corporations happened to open. Anywhere that order can pick which
 * subscription a family re-evaluates or copies, it has to be a promise
 * (`docs/determinism.md` rule 4). `ALL_INDUSTRIES` is that promise.
 */
function subscribedIndustries(family: Family): IndustryType[] {
  return ALL_INDUSTRIES.filter(industry => family.subscriptions[industry] !== undefined);
}

/** The corporations a family subscribes to, in industry order. */
function subscribedCorpIds(family: Family): number[] {
  return subscribedIndustries(family).map(industry => family.subscriptions[industry]);
}

export class HouseManager {
  private houses: House[] = [];
  private families: Map<number, Family> = new Map();
  private grid: TerrainGrid;
  private events: EventBus;
  private rng: Rng;
  /** Sim-time reader, in milliseconds. See `ParkingtonNewsItem.timestamp`. */
  private now: () => number;
  private nextHouseId = 0;
  private nextFamilyId = 0;
  private spawnTimer = 0;
  private familyUpdateTimer = 0;
  private pendingCarRequests: CarRequest[] = [];
  private news: ParkingtonNewsItem[] = [];
  private config: HouseManagerConfig;

  // Callback for economy visit payments
  onFamilyVisit: ((familyId: number, houseId: number) => void) | null = null;

  // Callback for business grant when a new corporation spawns.
  // Returns true if grant was paid, false if not affordable.
  onCorpSpawning: (() => boolean) | null = null;

  /**
   * Who is willing to move in, and with what.
   *
   * Wired once by `SurfaceSim`, the way the two callbacks above are, because the
   * questions it answers — *is this house lit* and *what is this district worth
   * to live in* — are ones this class has no way to ask. Left `null` by a caller
   * that has not wired one, in which case every vacancy is admitted and every
   * newcomer arrives with the standard purse: exactly the behaviour Parkington
   * had before ADR-019.
   */
  market: HousingMarket | null = null;

  /**
   * Which ground is spoken for.
   *
   * The third frozen adapter, wired once by `SurfaceSim` beside the other two. It
   * answers one question — *has the mayor deeded this cell wild* — and it is the
   * whole mechanical weight of §10's meadow covenant on this class: a deeded cell is
   * not a candidate for a house and not a cell a footprint may extend into.
   *
   * That refusal is where the covenant's price actually lands. The dollars are a
   * ledger line nothing reads (`MercyManager`); the cells are growth this district
   * will not have — fewer houses, fewer corporations, less tax, permanently. A mayor
   * who deeds the buildable ring around their roads has bought a smaller town, and
   * nothing anywhere will tell them what it cost.
   *
   * Left `null` by a caller that has not wired one, in which case every cell is
   * available: Parkington before ADR-020, which is every district in the corpus.
   */
  land: { isDeeded: (x: number, y: number) => boolean } | null = null;

  // Building footprint templates with weights (smaller = more common)
  private static readonly FOOTPRINTS: { shape: FootprintShape; weight: number }[] = [
    // 1x1 (very common)
    { shape: [{ dx: 0, dy: 0 }], weight: 40 },
    // 1x2 horizontal
    { shape: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }], weight: 20 },
    // 1x2 vertical
    { shape: [{ dx: 0, dy: 0 }, { dx: 0, dy: 1 }], weight: 20 },
    // 2x2 square
    { shape: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 }], weight: 10 },
    // L-shape (3 cells)
    { shape: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 0, dy: 1 }], weight: 5 },
    // 1x3 horizontal
    { shape: [{ dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 2, dy: 0 }], weight: 3 },
    // 3x3 (rare)
    { shape: [
      { dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 2, dy: 0 },
      { dx: 0, dy: 1 }, { dx: 1, dy: 1 }, { dx: 2, dy: 1 },
      { dx: 0, dy: 2 }, { dx: 1, dy: 2 }, { dx: 2, dy: 2 }
    ], weight: 2 },
  ];

  // House colors - suburban palette
  private static readonly WALL_COLORS = [
    '#e8dcc8', // Cream
    '#d4c4a8', // Tan
    '#c9b896', // Beige
    '#b8c4c8', // Light blue-gray
    '#d4d4c8', // Off-white
    '#e0d0b8', // Warm white
    '#c8b8a0', // Sand
  ];

  private static readonly ROOF_COLORS = [
    '#5a4a3a', // Dark brown
    '#4a5a5a', // Slate gray
    '#6a4a3a', // Terracotta
    '#3a4a4a', // Charcoal
    '#5a5a4a', // Olive gray
    '#7a5a4a', // Clay
  ];

  // Corporate building colors - more modern/industrial
  private static readonly CORP_WALL_COLORS = [
    '#8899aa', // Steel blue
    '#7a8a8a', // Industrial gray
    '#6a7a8a', // Slate
    '#8a9090', // Concrete
    '#707888', // Blue steel
  ];


  // Family last names - diverse suburban mix
  private static readonly FAMILY_NAMES = [
    'Johnson', 'Martinez', 'Kim', 'Patel', 'Garcia', 'Nguyen', 'Smith', 'Chen',
    'Williams', 'Singh', 'Brown', 'Lee', 'Jones', 'Müller', 'Taylor', 'Ahmed',
    'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Robinson', 'Clark', 'Lewis',
    'Walker', 'Hall', 'Young', 'Allen', 'King', 'Wright', 'Lopez', 'Hill',
    'Scott', 'Green', 'Adams', 'Baker', 'Nelson', 'Carter', 'Mitchell', 'Perez',
    'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker', 'Evans', 'Edwards', 'Collins',
    'Tanaka', 'Okafor', 'Santos', 'Johansson', 'Petrov', 'Cohen', 'Dubois', 'O\'Brien',
  ];

  private generateFamilyName(): string {
    return this.rng.pick(HouseManager.FAMILY_NAMES)!;
  }

  constructor(
    grid: TerrainGrid,
    events: EventBus,
    rng: Rng,
    now: () => number,
    config: Partial<HouseManagerConfig> = {}
  ) {
    this.grid = grid;
    this.events = events;
    this.rng = rng;
    this.now = now;
    this.config = { ...DEFAULT_HOUSE_CONFIG, ...config };
  }

  getConfig(): HouseManagerConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<HouseManagerConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /**
   * `shed` names the buildings the grid switched off at the last metered cycle.
   *
   * Only construction reads it: a site with no power does not build that cycle
   * (`DESIGN-GOVERNORS.md` §5 lists construction among power's consumers). A
   * *finished* building that is dark is recorded and drawn unlit and is not
   * otherwise punished here — the design puts that consequence in quality of
   * life and migration (§10), which owns its own arithmetic.
   *
   * Omitted entirely while nothing is shed, which is every tick of a district
   * that fits under its supply.
   *
   * `jobs` names who works for the polity, and is omitted on the same terms:
   * while nobody in the district holds a federal job there is nothing to ask.
   */
  update(
    dt: number,
    hasPavedCells: boolean,
    shed?: { isShed(houseId: number): boolean },
    jobs?: CommuteSource
  ): void {
    if (!hasPavedCells) return;

    // Try to spawn new houses periodically. Timers carry their remainder so the
    // development rate is a function of sim-time rather than of tick size
    // (ADR-002 §3).
    const spawn = advanceTimer(this.spawnTimer, dt, this.config.SPAWN_INTERVAL, 1);
    this.spawnTimer = spawn.value;
    if (spawn.fires > 0) {
      this.trySpawnHouse();
    }

    // Update building progress
    for (const house of this.houses) {
      if (!house.built) {
        if (shed?.isShed(house.id)) continue;
        house.buildProgress += this.config.BUILD_SPEED * dt;
        if (house.buildProgress >= 1) {
          house.buildProgress = 1;
          house.built = true;
          this.events.emit('NOTIFICATION_SHOW', {
            message: 'New development complete!',
            type: 'success',
          });
        }
      }
    }

    // Update family activities
    const familyTick = advanceTimer(this.familyUpdateTimer, dt, this.config.FAMILY_UPDATE_INTERVAL, 1);
    this.familyUpdateTimer = familyTick.value;
    if (familyTick.fires > 0) {
      this.updateFamilies(jobs);
    }

    // Update visiting families — including the ones whose visit is a shift.
    // A family clocks off exactly the way it leaves a shop: the timer runs out
    // and it asks for a lift home.
    for (const family of this.families.values()) {
      if (family.state === 'visiting') {
        family.visitTimer += dt;
        if (family.visitTimer >= family.visitDuration) {
          // Time to go home - request a car
          this.requestCarForFamily(family, family.homeHouseId);
        }
      }
    }
  }

  /**
   * Update family decision-making.
   */
  private updateFamilies(jobs?: CommuteSource): void {
    const builtHouses = this.houses.filter(h => h.built);
    if (builtHouses.length < 2) return; // Need at least 2 houses for visits

    for (const family of this.families.values()) {
      // Subscription re-evaluation (can happen regardless of family state)
      if (this.rng.chance(this.config.SWITCH_CHANCE)) {
        this.tryReEvaluateSubscription(family);
      }

      if (family.state !== 'home') continue;

      // Work comes before the shops, which is the whole of what a job does to a
      // family's day here. The draw happens only for a family that actually has
      // somewhere to be — an unemployed family in a district with a data centre
      // makes exactly the decisions it made before there was one, and a district
      // with no facility never reaches this branch at all.
      if (jobs) {
        const facilityId = jobs.jobOf(family.id);
        if (facilityId !== null && !jobs.isWithdrawn(facilityId)) {
          if (this.rng.chance(this.config.COMMUTE_CHANCE)) {
            this.requestCarToFacility(family, facilityId);
            continue;
          }
        }
      }

      // Higher chance to visit a subscribed corporation
      const subIds = subscribedCorpIds(family);
      if (subIds.length > 0 && this.rng.chance(this.config.SUBSCRIPTION_VISIT_CHANCE)) {
        // Pick a random subscription to visit
        const validSubs = subIds.filter(subId => {
          const house = this.houses.find(h => h.id === subId);
          return house && house.built;
        });

        const destination = this.rng.pick(validSubs);
        if (destination !== undefined) {
          this.requestCarForFamily(family, destination);
          continue;
        }
      }

      // Random chance to want to visit somewhere else
      if (this.rng.chance(this.config.VISIT_CHANCE)) {
        // Pick a random destination (not their own home)
        const destinations = builtHouses.filter(h => h.id !== family.homeHouseId);
        const destination = this.rng.pick(destinations);
        if (destination === undefined) continue;

        this.requestCarForFamily(family, destination.id);
      }
    }
  }

  /**
   * Generate subscriptions for a new family: one corp per industry.
   */
  private generateSubscriptionsForFamily(family: Family): void {
    for (const industry of ALL_INDUSTRIES) {
      // Find all built corporations of this industry
      const corps = this.houses.filter(
        h => h.occupant === 'corporation' && h.built && h.industryType === industry
      );
      // Pick one randomly. The draw happens whether or not any corporation of
      // this industry exists, so that a family's stream position does not depend
      // on which industries are currently open.
      const corp = this.rng.pick(corps);
      if (corp) {
        family.subscriptions[industry] = corp.id;
        // Grant 1 share on subscription
        this.grantFamilyShare(family.id, corp.id);
      }
    }
  }

  /**
   * Generate subscribers for a new corporation from existing families.
   * Families that don't have a subscription for this industry get one.
   */
  private generateSubscribersForCorporation(corpHouseId: number, industryType: IndustryType): void {
    for (const family of this.families.values()) {
      if (family.subscriptions[industryType] === undefined) {
        family.subscriptions[industryType] = corpHouseId;
        // Grant 1 share on subscription
        this.grantFamilyShare(family.id, corpHouseId);
      }
    }
  }

  /**
   * Grant 1-3 random shares in existing built corporations to a new family.
   */
  private grantInitialFamilyShares(family: Family): void {
    const builtCorps = this.houses.filter(h => h.occupant === 'corporation' && h.built);
    if (builtCorps.length === 0) return;
    const shareCount = 1 + this.rng.int(3); // 1-3
    for (let i = 0; i < shareCount; i++) {
      const corp = this.rng.pick(builtCorps)!;
      this.grantFamilyShare(family.id, corp.id);
    }
  }

  /**
   * Grant cross-ownership shares when a new corporation is created.
   * 2-4 existing corps get 1 share in the new corp; new corp gets 1 share in 1-2 existing corps.
   */
  private grantCrossOwnershipShares(newCorpHouseId: number): void {
    const existingCorps = this.houses.filter(h => h.id !== newCorpHouseId && h.occupant === 'corporation' && h.built);
    if (existingCorps.length === 0) return;

    // 2-4 existing corps get 1 share in the new corp
    const investorCount = Math.min(existingCorps.length, 2 + this.rng.int(3));
    const investors = this.shuffled(existingCorps);
    for (let i = 0; i < investorCount; i++) {
      this.grantCorpShare(investors[i].id, newCorpHouseId);
    }

    // New corp gets 1 share in 1-2 existing corps
    const ownedCount = Math.min(existingCorps.length, 1 + this.rng.int(2));
    const owned = this.shuffled(existingCorps);
    for (let i = 0; i < ownedCount; i++) {
      this.grantCorpShare(newCorpHouseId, owned[i].id);
    }
  }

  /**
   * Fisher-Yates, drawing from the injected generator.
   *
   * Replaces `[...arr].sort(() => Math.random() - 0.5)`, which was not a
   * shuffle: a comparator that answers inconsistently gives an implementation
   * -defined permutation biased toward leaving elements near where they started.
   * Which corporations end up owning a new one was therefore skewed toward the
   * oldest ones, and the reason was a one-liner nobody was going to question.
   */
  private shuffled<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.rng.int(i + 1);
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  /**
   * Re-evaluate one random subscription. Switch to closest corp if better option exists.
   */
  private tryReEvaluateSubscription(family: Family): void {
    // Pick one random subscription to re-evaluate. The draw happens even for a
    // family with no subscriptions, so an unsubscribed family does not shift
    // every later family's decisions.
    const industry = this.rng.pick(subscribedIndustries(family));
    if (industry === undefined) return;
    const currentCorpId = family.subscriptions[industry];

    // Find all built corps of this industry
    const candidates = this.houses.filter(
      h => h.occupant === 'corporation' && h.built && h.industryType === industry
    );
    if (candidates.length <= 1) return; // No alternatives

    // Find home position for distance calc
    const homeHouse = this.houses.find(h => h.id === family.homeHouseId);
    if (!homeHouse) return;

    // Find closest corp by Manhattan distance
    let bestCorp = candidates[0];
    let bestDist = Math.abs(bestCorp.gridX - homeHouse.gridX) + Math.abs(bestCorp.gridY - homeHouse.gridY);
    for (let i = 1; i < candidates.length; i++) {
      const dist = Math.abs(candidates[i].gridX - homeHouse.gridX) + Math.abs(candidates[i].gridY - homeHouse.gridY);
      if (dist < bestDist) {
        bestDist = dist;
        bestCorp = candidates[i];
      }
    }

    if (bestCorp.id === currentCorpId) return; // Already at closest

    // Switch!
    family.subscriptions[industry] = bestCorp.id;
    this.grantFamilyShare(family.id, bestCorp.id);

    const config = INDUSTRY_CONFIGS[industry];
    this.addNews(`The ${family.name}s switched to a closer ${config.label}`, 'switch');
  }

  /**
   * Request a car for a family to travel to a building.
   */
  private requestCarForFamily(family: Family, destinationHouseId: number): void {
    this.requestCar(family, destinationHouseId, null);
  }

  /** Request a car to a facility gate: a family going on shift. */
  private requestCarToFacility(family: Family, facilityId: number): void {
    this.requestCar(family, null, facilityId);
  }

  /**
   * Queue one trip. Exactly one end of it is a building; the other may be a gate.
   *
   * The pickup is wherever the family actually is, which since ADR-016 is not
   * necessarily a house: a family clocking off is standing on somebody else's
   * concrete, and the car has to come there.
   */
  private requestCar(
    family: Family,
    destinationHouseId: number | null,
    destinationFacilityId: number | null
  ): void {
    if (family.state === 'waiting_for_car' || family.state === 'in_car') return;

    family.state = 'waiting_for_car';
    family.targetHouseId = destinationHouseId;

    const request: CarRequest = {
      familyId: family.id,
      pickupHouseId: family.atFacilityId === null ? family.currentHouseId : null,
      destinationHouseId,
    };
    if (family.atFacilityId !== null) request.pickupFacilityId = family.atFacilityId;
    if (destinationFacilityId !== null) request.destinationFacilityId = destinationFacilityId;

    this.pendingCarRequests.push(request);
  }

  /**
   * Get pending car requests for CarManager to process.
   */
  getPendingCarRequests(): CarRequest[] {
    return this.pendingCarRequests;
  }

  /**
   * Clear a car request after it's been assigned.
   */
  clearCarRequest(familyId: number): void {
    this.pendingCarRequests = this.pendingCarRequests.filter(r => r.familyId !== familyId);
  }

  /**
   * Called when a car arrives to pick up a family.
   */
  familyPickedUp(familyId: number): void {
    const family = this.families.get(familyId);
    if (family) {
      family.state = 'in_car';
      // Somebody in a car is not standing in a data centre. The field is cleared
      // here rather than at the drop-off so that it means exactly what it says at
      // every tick — the pickup location was resolved when the trip was queued,
      // so nothing downstream still needs it.
      family.atFacilityId = null;
    }
  }

  /**
   * Called when a car drops off a family at their destination.
   */
  familyDroppedOff(familyId: number, houseId: number): void {
    const family = this.families.get(familyId);
    if (!family) return;

    family.currentHouseId = houseId;
    // Whatever they were doing, they are not at the data centre any more.
    family.atFacilityId = null;

    if (houseId === family.homeHouseId) {
      // Arrived home
      family.state = 'home';
      family.targetHouseId = null;
    } else {
      // Arrived at destination - start visiting
      family.state = 'visiting';
      family.visitTimer = 0;
      family.visitDuration = this.rng.range(
        this.config.MIN_VISIT_DURATION,
        this.config.MAX_VISIT_DURATION
      );

      // Notify economy of visit (for at-visit payments)
      if (this.onFamilyVisit) {
        this.onFamilyVisit(familyId, houseId);
      }

      // Social subscription copying: when visiting another family's home
      const visitedHouse = this.houses.find(h => h.id === houseId);
      if (visitedHouse && visitedHouse.familyHome && !visitedHouse.vacant) {
        this.trySocialCopy(family, houseId);
      }
    }
  }

  /**
   * A car has dropped a family at a facility gate: the shift starts.
   *
   * The sibling of `familyDroppedOff`, and pointedly not the same method. Being
   * dropped at a *building* triggers two things a shift must not: the economy is
   * told a customer arrived, and the family may copy a subscription off whoever
   * lives there. A data centre sells nothing and nobody lives in it. So a shift
   * is a visit with the commerce and the gossip removed, and what is left is a
   * timer and a car parked outside — which is exactly what a shift is.
   *
   * Nothing is paid here either. Wages are per budget cycle and per *job*, not
   * per arrival (`LabourManager.payroll`): a family that is between shifts is
   * still employed, and a family stuck in traffic has not been docked.
   */
  familyArrivedAtFacility(familyId: number, facilityId: number): void {
    const family = this.families.get(familyId);
    if (!family) return;

    family.atFacilityId = facilityId;
    family.state = 'visiting';
    family.targetHouseId = null;
    family.visitTimer = 0;
    family.visitDuration = this.rng.range(
      this.config.MIN_SHIFT_DURATION,
      this.config.MAX_SHIFT_DURATION
    );
  }

  /**
   * Get a family by ID.
   */
  getFamily(familyId: number): Family | undefined {
    return this.families.get(familyId);
  }

  /**
   * Called when a car fails to pick up a family (timeout).
   */
  familyCancelledPickup(familyId: number): void {
    const family = this.families.get(familyId);
    if (!family || family.state !== 'waiting_for_car') return;

    family.targetHouseId = null;
    if (family.atFacilityId !== null) {
      // Nobody came. They are still standing at the gate, so they go back to
      // waiting rather than being declared home — the alternative is a family
      // that is at work and at home at once, and the next timeout would file
      // that contradiction into the hash.
      family.state = 'visiting';
      family.visitTimer = 0;
      family.visitDuration = this.rng.range(
        this.config.MIN_SHIFT_DURATION,
        this.config.MAX_SHIFT_DURATION
      );
      return;
    }
    family.state = 'home';
  }

  /**
   * Get house by ID.
   */
  getHouseById(houseId: number): House | undefined {
    return this.houses.find(h => h.id === houseId);
  }

  private trySpawnHouse(): void {
    // Try to fill a vacant home first (50% chance to prioritize reuse). The draw
    // is taken whether or not there is anything to fill, so a district with no
    // vacancies consumes exactly the random numbers it always did.
    if (this.rng.chance(0.5) && this.tryFillVacantHome(this.market ?? undefined)) return;

    const location = this.findHouseLocation();
    if (!location) return;

    const footprintData = this.pickFootprint();
    const footprint = footprintData.shape;

    // Check if footprint fits at location (all cells must be NATURAL, and none of
    // them deeded wild — a covenant is a promise about a cell, and a house on the
    // cell next door is entitled to overhang everything except that)
    for (const cell of footprint) {
      const cx = location.x + cell.dx;
      const cy = location.y + cell.dy;
      const checkCell = this.grid.getCell(cx, cy);
      if (!checkCell || checkCell.state !== CellState.NATURAL) {
        return; // Doesn't fit
      }
      if (this.land?.isDeeded(cx, cy)) {
        return; // Spoken for
      }
    }

    // Floors correlate with footprint size (more cells = taller)
    const floors = Math.min(4, Math.ceil(footprint.length / 2));

    // Determine occupant type: 70% families, 30% corporations
    // Larger buildings more likely to be corporations
    const corpChance = 0.3 + (floors - 1) * 0.15; // 30% base, +15% per floor
    const occupant: OccupantType = this.rng.chance(corpChance) ? 'corporation' : 'family';

    const houseId = this.nextHouseId++;

    // Use different colors for corporations vs families
    const isCorp = occupant === 'corporation';

    // Assign industry type for corporations (weighted toward underrepresented)
    let assignedIndustry: IndustryType | undefined;
    if (isCorp) {
      assignedIndustry = this.pickIndustryType();
    }

    const wallColor = isCorp
      ? this.rng.pick(HouseManager.CORP_WALL_COLORS)!
      : this.rng.pick(HouseManager.WALL_COLORS)!;
    const roofColor = isCorp
      ? '#3a4550' // Flat dark roof for corps
      : this.rng.pick(HouseManager.ROOF_COLORS)!;
    const accentColor = isCorp && assignedIndustry
      ? INDUSTRY_CONFIGS[assignedIndustry].accentColor
      : '#ffffff';

    // Corporations get extra floors
    const finalFloors = isCorp ? Math.min(6, floors + 2) : floors;

    const house: House = {
      id: houseId,
      gridX: location.x,
      gridY: location.y,
      footprint,
      buildProgress: 0,
      color: wallColor,
      roofColor: roofColor,
      accentColor: accentColor,
      floors: finalFloors,
      built: false,
      occupant,
      familyHome: occupant === 'family',
      vacant: false,
      industryType: assignedIndustry,
      revenue: 0,
      taxesPaid: 0,
      familyShares: {},
      corpShares: {},
      dividendsPaid: 0,
    };

    this.houses.push(house);

    // Create a family for family homes
    if (occupant === 'family') {
      const familyName = this.generateFamilyName();
      const family: Family = {
        id: this.nextFamilyId++,
        name: familyName,
        homeHouseId: houseId,
        currentHouseId: houseId,
        state: 'home',
        targetHouseId: null,
        visitTimer: 0,
        visitDuration: 0,
        // A new house in a district that is coming apart attracts whoever can
        // afford a house in a district that is coming apart. Exactly 1 above the
        // market's plateau, which every ordinary Parkington sits well above.
        cash: EconomyManager.getInitialFamilyCash() * (this.market?.newcomerScale() ?? 1),
        subscriptions: {},
        recurringPaymentTimers: {},
        shares: {},
        hardshipCycles: 0,
        atFacilityId: null,
      };

      // Subscribe to one corp per industry
      this.generateSubscriptionsForFamily(family);

      // Grant 1-3 random shares in existing built corporations
      this.grantInitialFamilyShares(family);

      this.families.set(family.id, family);

      this.addNews(`The ${familyName}s moved to Parkington!`, 'move_in');
    } else {
      // New corporation - families without this industry get subscribed
      this.generateSubscribersForCorporation(houseId, assignedIndustry!);

      // Cross-ownership: 2-4 existing corps get 1 share in the new corp
      this.grantCrossOwnershipShares(houseId);

      // Try to pay business grant
      const config = INDUSTRY_CONFIGS[assignedIndustry!];
      if (this.onCorpSpawning) {
        const grantPaid = this.onCorpSpawning();
        if (!grantPaid) {
          house.buildProgress = -0.3; // 30% slower construction
          this.addNews(`New ${config.label} opening — no government grant available`, 'business');
        } else {
          this.addNews(`New ${config.label} opening soon`, 'business');
        }
      } else {
        this.addNews(`New ${config.label} opening soon`, 'business');
      }
    }

    // Mark the grid cells under the house (keep them NATURAL visually, house covers them)
    // Don't change state - house is built ON green
  }

  /**
   * Count connected paved cells from a starting paved cell using small BFS.
   * Used to verify a paved neighbor is part of a real road network, not an isolated patch.
   */
  private countConnectedPavedCells(startX: number, startY: number, maxCount = 5): number {
    const cell = this.grid.getCell(startX, startY);
    if (!cell || !this.isPaved(cell.state)) return 0;

    const visited = new Set<string>();
    const queue: { x: number; y: number }[] = [{ x: startX, y: startY }];
    visited.add(`${startX},${startY}`);

    const directions = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    let head = 0;
    while (head < queue.length && visited.size < maxCount) {
      const cur = queue[head++];
      for (const dir of directions) {
        const nx = cur.x + dir.dx;
        const ny = cur.y + dir.dy;
        const key = `${nx},${ny}`;
        if (visited.has(key)) continue;
        const neighbor = this.grid.getCell(nx, ny);
        if (neighbor && this.isPaved(neighbor.state)) {
          visited.add(key);
          queue.push({ x: nx, y: ny });
        }
      }
    }

    return visited.size;
  }

  private findHouseLocation(): { x: number; y: number } | null {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();
    const candidates: { x: number; y: number; score: number }[] = [];

    // Cardinal directions for adjacency check
    const cardinals = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    // Find NATURAL cells that are near paved areas
    for (let y = 1; y < rows - 3; y++) {
      for (let x = 1; x < cols - 3; x++) {
        const cell = this.grid.getCell(x, y);
        if (!cell) continue;

        // Must be NATURAL (green/undeveloped)
        if (cell.state !== CellState.NATURAL) continue;

        // And not deeded wild. Skipped before the road check rather than after,
        // so a covenanted cell never enters the candidate list and therefore never
        // shifts the draw that picks between the ones that did.
        if (this.land?.isDeeded(x, y)) continue;

        // Check if already has a house nearby
        if (this.hasHouseNear(x, y)) continue;

        // Require at least 1 cardinal paved neighbor at distance 1
        // that is connected to a road network (>= 3 connected paved cells)
        let hasConnectedRoad = false;
        for (const dir of cardinals) {
          const nx = x + dir.dx;
          const ny = y + dir.dy;
          const neighbor = this.grid.getCell(nx, ny);
          if (neighbor && this.isPaved(neighbor.state)) {
            if (this.countConnectedPavedCells(nx, ny) >= 3) {
              hasConnectedRoad = true;
              break;
            }
          }
        }

        if (!hasConnectedRoad) continue;

        // Count paved cells in 5x5 area for scoring
        let pavedNeighbors = 0;
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const neighbor = this.grid.getCell(x + dx, y + dy);
            if (neighbor && this.isPaved(neighbor.state)) {
              pavedNeighbors++;
            }
          }
        }

        // Cap at 12 to avoid placing in the middle of huge parking lots
        if (pavedNeighbors <= 12) {
          const propValue = this.getPropertyValue(x, y);
          candidates.push({ x, y, score: pavedNeighbors + propValue * 0.1 });
        }
      }
    }

    if (candidates.length === 0) return null;

    // Prefer locations with moderate paved neighbors.
    // Pick from top candidates with some randomness. Ties in score are broken by
    // position so that two runs agree on the order before the draw is made —
    // `Array.prototype.sort` is stable, but the input order it stabilises is the
    // grid scan order, which is fine, and this makes that explicit.
    candidates.sort((a, b) => b.score - a.score || a.y - b.y || a.x - b.x);
    const topCandidates = candidates.slice(0, Math.min(15, candidates.length));
    const choice = this.rng.pick(topCandidates)!;

    return { x: choice.x, y: choice.y };
  }

  /**
   * Whether a building stands on this exact cell.
   *
   * Exact, unlike `hasHouseNear`, which keeps new houses from crowding old ones
   * and therefore asks about a two-cell margin. This question has one caller and
   * one purpose: a governor's `FORCE_PLACE` needs to know which cells of its
   * rectangle already have somebody's roof on them (ADR-015 D86). §7 says those
   * houses should be *demolished* and their families displaced; this build has
   * neither mechanic, so the siting is refused instead and the refusal is
   * documented as a deferral rather than a rule.
   */
  occupiesCell(x: number, y: number): boolean {
    for (const house of this.houses) {
      for (const cell of house.footprint) {
        if (house.gridX + cell.dx === x && house.gridY + cell.dy === y) return true;
      }
    }
    return false;
  }

  private hasHouseNear(x: number, y: number): boolean {
    return this.houses.some(h => {
      // Check distance from any cell in footprint
      for (const cell of h.footprint) {
        const hx = h.gridX + cell.dx;
        const hy = h.gridY + cell.dy;
        if (Math.abs(hx - x) < 2 && Math.abs(hy - y) < 2) {
          return true;
        }
      }
      return false;
    });
  }

  private isPaved(state: CellState): boolean {
    return (
      state === CellState.POURED ||
      state === CellState.CURED ||
      state === CellState.PAINTED
    );
  }

  private pickFootprint(): { shape: FootprintShape; weight: number } {
    const totalWeight = HouseManager.FOOTPRINTS.reduce((sum, f) => sum + f.weight, 0);
    let random = this.rng.next() * totalWeight;

    for (const footprint of HouseManager.FOOTPRINTS) {
      random -= footprint.weight;
      if (random <= 0) {
        return footprint;
      }
    }

    return HouseManager.FOOTPRINTS[0];
  }

  /**
   * Get all houses with their depth values, for the renderer's depth sort.
   * Depth is calculated as the maximum Y of the footprint (front edge in isometric view).
   */
  getHousesWithDepth(): { house: House; depth: number }[] {
    return this.houses.map(house => {
      // Calculate maximum Y from footprint (the front edge)
      let maxY = house.gridY;
      for (const cell of house.footprint) {
        maxY = Math.max(maxY, house.gridY + cell.dy);
      }
      return { house, depth: maxY };
    });
  }

  /**
   * Pick an industry type, weighted toward underrepresented industries.
   */
  private pickIndustryType(): IndustryType {
    // Count existing corps per industry
    const counts: Record<string, number> = {};
    for (const industry of ALL_INDUSTRIES) {
      counts[industry] = 0;
    }
    for (const house of this.houses) {
      if (house.occupant === 'corporation' && house.industryType) {
        counts[house.industryType] = (counts[house.industryType] || 0) + 1;
      }
    }

    // Weight inversely proportional to count (fewer = more likely)
    const maxCount = Math.max(1, ...Object.values(counts));
    const weights = ALL_INDUSTRIES.map(industry => maxCount + 1 - counts[industry]);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);

    let random = this.rng.next() * totalWeight;
    for (let i = 0; i < ALL_INDUSTRIES.length; i++) {
      random -= weights[i];
      if (random <= 0) return ALL_INDUSTRIES[i];
    }
    return ALL_INDUSTRIES[0];
  }

  /**
   * Small chance to copy one subscription from the host family when visiting.
   */
  private trySocialCopy(visitor: Family, hostHouseId: number): void {
    if (!this.rng.chance(0.1)) return; // 10% chance

    // Find host family
    const hostFamily = Array.from(this.families.values()).find(f => f.homeHouseId === hostHouseId);
    if (!hostFamily || hostFamily.id === visitor.id) return;

    // Find subscriptions the host has that differ from visitor's, in industry
    // order rather than in whatever order the host happened to subscribe.
    const copyable: { industry: IndustryType; corpId: number }[] = [];
    for (const industry of subscribedIndustries(hostFamily)) {
      const corpId = hostFamily.subscriptions[industry];
      if (visitor.subscriptions[industry] !== undefined && visitor.subscriptions[industry] !== corpId) {
        copyable.push({ industry, corpId });
      }
    }

    // Copy one random subscription
    const pick = this.rng.pick(copyable);
    if (!pick) return;
    visitor.subscriptions[pick.industry] = pick.corpId;
    this.grantFamilyShare(visitor.id, pick.corpId);

    const config = INDUSTRY_CONFIGS[pick.industry];
    this.addNews(`The ${visitor.name}s heard about a ${config.label} from the ${hostFamily.name}s`, 'switch');
  }

  // --- Property value ---

  /**
   * Compute property value for a cell (0-100). Higher is better.
   * Based on: nearby natural cells (green space), nearby built corps (convenience), road access.
   */
  getPropertyValue(x: number, y: number): number {
    let naturalCount = 0;
    let corpCount = 0;
    let roadAccess = false;

    // Check 5x5 area
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const cell = this.grid.getCell(x + dx, y + dy);
        if (!cell) continue;
        if (cell.state === CellState.NATURAL) naturalCount++;
        if (this.isPaved(cell.state) && Math.abs(dx) <= 1 && Math.abs(dy) <= 1) roadAccess = true;
      }
    }

    // Count built corps within 7x7
    for (const house of this.houses) {
      if (house.occupant !== 'corporation' || !house.built) continue;
      const dist = Math.abs(house.gridX - x) + Math.abs(house.gridY - y);
      if (dist <= 5) corpCount++;
    }

    // Score: green space (0-40) + road access (0-25) + corp proximity (0-35)
    const greenScore = Math.min(40, naturalCount * 3);
    const roadScore = roadAccess ? 25 : 0;
    const corpScore = Math.min(35, corpCount * 8);

    return Math.min(100, greenScore + roadScore + corpScore);
  }

  // --- Move-out / move-in methods ---

  /**
   * Remove a family and mark their home as vacant.
   *
   * `reason` is what the ticker prints after the family's name. It defaults to
   * the line this method has printed since before there was a polity, because
   * that line *is* the insolvency cause and a district that never had a shortage
   * should read exactly as it always did. §10's promise — a migration with a
   * named, measured cause — is the caller supplying a different one.
   */
  removeFamilyById(familyId: number, reason = `couldn't make ends meet`): void {
    const family = this.families.get(familyId);
    if (!family) return;
    const house = this.houses.find(h => h.id === family.homeHouseId);
    if (house) house.vacant = true;
    // Clean up shareholder records
    for (const [corpId, count] of shareEntries(family.shares)) {
      const corp = this.houses.find(h => h.id === corpId);
      if (corp && corp.familyShares[familyId]) {
        corp.familyShares[familyId] -= count;
        if (corp.familyShares[familyId] <= 0) delete corp.familyShares[familyId];
      }
    }
    this.addNews(`The ${family.name}s left Parkington — ${reason}`, 'economy');
    this.families.delete(familyId);
  }

  /**
   * Write a household's strain streak back onto the family.
   *
   * `WelfareManager` decides it and cannot see a family, so the streak travels
   * back the way the reading travelled out: through `SurfaceSim`, as a number
   * against an id. The alternative — moving the counter into the measure — would
   * take `hardshipCycles` out of the hash and re-record every golden master in
   * the repository for a field that means what it always meant.
   */
  setHardshipCycles(familyId: number, cycles: number): void {
    const family = this.families.get(familyId);
    if (family) family.hardshipCycles = cycles;
  }

  /**
   * Try to move a new family into a vacant home. Returns true if successful.
   */
  tryFillVacantHome(market?: HousingMarket): boolean {
    const vacant = this.houses.filter(h => h.familyHome && h.vacant && h.built);
    if (vacant.length === 0) return false;

    // Nobody rents a house with no power and no water. The gate is the arrival
    // half of §10 and it is what lets a district's population actually fall: in
    // a total blackout every vacancy is refused, so the houses the departures
    // empty stay empty. Before ADR-019 this filled one home a cycle regardless,
    // and a failing district read as stable while it churned through households.
    const vacantHome = market ? vacant.find(h => market.admits(h.id)) : vacant[0];
    if (!vacantHome) {
      market?.refused();
      return false;
    }

    vacantHome.vacant = false;
    const familyName = this.generateFamilyName();
    const family: Family = {
      id: this.nextFamilyId++,
      name: familyName,
      homeHouseId: vacantHome.id,
      currentHouseId: vacantHome.id,
      state: 'home',
      targetHouseId: null,
      visitTimer: 0,
      visitDuration: 0,
      cash: EconomyManager.getInitialFamilyCash() * (market?.newcomerScale() ?? 1),
      subscriptions: {},
      recurringPaymentTimers: {},
      shares: {},
      hardshipCycles: 0,
      atFacilityId: null,
    };
    this.generateSubscriptionsForFamily(family);
    this.grantInitialFamilyShares(family);
    this.families.set(family.id, family);
    this.addNews(`The ${familyName}s moved into a vacant home!`, 'move_in');
    return true;
  }

  // --- News methods ---

  addNews(message: string, type: ParkingtonNewsItem['type']): void {
    this.news.push({ message, timestamp: this.now(), type });
    if (this.news.length > 50) this.news.shift();
  }

  getRecentNews(count: number): ParkingtonNewsItem[] {
    return this.news.slice(-count).reverse();
  }

  // --- Share management methods ---

  grantFamilyShare(familyId: number, corpHouseId: number, count = 1): void {
    const family = this.families.get(familyId);
    if (!family) return;
    const house = this.houses.find(h => h.id === corpHouseId);
    if (!house) return;
    family.shares[corpHouseId] = (family.shares[corpHouseId] || 0) + count;
    house.familyShares[familyId] = (house.familyShares[familyId] || 0) + count;
  }

  grantCorpShare(ownerHouseId: number, targetHouseId: number, count = 1): void {
    const target = this.houses.find(h => h.id === targetHouseId);
    if (!target) return;
    target.corpShares[ownerHouseId] = (target.corpShares[ownerHouseId] || 0) + count;
  }

  getHouseShareholderInfo(houseId: number): { familyShares: Record<number, number>; corpShares: Record<number, number>; totalShares: number } {
    const house = this.houses.find(h => h.id === houseId);
    if (!house) return { familyShares: {}, corpShares: {}, totalShares: 0 };
    const familyTotal = Object.values(house.familyShares).reduce((s, v) => s + v, 0);
    const corpTotal = Object.values(house.corpShares).reduce((s, v) => s + v, 0);
    return { familyShares: house.familyShares, corpShares: house.corpShares, totalShares: familyTotal + corpTotal };
  }

  getHouseDividendsPaid(houseId: number): number {
    return this.houses.find(h => h.id === houseId)?.dividendsPaid ?? 0;
  }

  addHouseDividendsPaid(houseId: number, amount: number): void {
    const house = this.houses.find(h => h.id === houseId);
    if (house) house.dividendsPaid += amount;
  }

  getFamilyShareCount(familyId: number): number {
    const family = this.families.get(familyId);
    if (!family) return 0;
    return Object.keys(family.shares).length;
  }

  // --- Economy accessor methods (used by EconomyManager) ---

  getAllFamilies(): Family[] {
    return Array.from(this.families.values());
  }

  getFamilyCash(familyId: number): number {
    return this.families.get(familyId)?.cash ?? 0;
  }

  addFamilyCash(familyId: number, amount: number): void {
    const family = this.families.get(familyId);
    if (family) family.cash += amount;
  }

  getRecurringTimer(familyId: number, industry: string): number {
    return this.families.get(familyId)?.recurringPaymentTimers[industry] ?? 0;
  }

  setRecurringTimer(familyId: number, industry: string, value: number): void {
    const family = this.families.get(familyId);
    if (family) family.recurringPaymentTimers[industry] = value;
  }

  getHouseRevenue(houseId: number): number {
    const house = this.houses.find(h => h.id === houseId);
    return house?.revenue ?? 0;
  }

  addHouseRevenue(houseId: number, amount: number): void {
    const house = this.houses.find(h => h.id === houseId);
    if (house) house.revenue += amount;
  }

  deductHouseRevenue(houseId: number, amount: number): void {
    const house = this.houses.find(h => h.id === houseId);
    if (house) house.revenue = Math.max(0, house.revenue - amount);
  }

  addHouseTaxesPaid(houseId: number, amount: number): void {
    const house = this.houses.find(h => h.id === houseId);
    if (house) house.taxesPaid += amount;
  }

  getHouseIndustry(houseId: number): IndustryType | undefined {
    return this.houses.find(h => h.id === houseId)?.industryType;
  }

  /**
   * Get count of subscriptions a family has.
   */
  getFamilySubscriptionCount(familyId: number): number {
    const family = this.families.get(familyId);
    if (!family) return 0;
    return Object.keys(family.subscriptions).length;
  }

  /**
   * Get subscriber count for a corporation.
   */
  getSubscriberCount(houseId: number): number {
    let count = 0;
    for (const family of this.families.values()) {
      // `subscribedIndustries` rather than `Object.values(family.subscriptions)`:
      // the answer cannot depend on the order either way — it is an existence
      // check — but the named order is the one this file already promises, and a
      // reader should not have to work out which of the two loops is safe.
      if (subscribedIndustries(family).some(i => family.subscriptions[i] === houseId)) {
        count++;
      }
    }
    return count;
  }

  getHouseCount(): number {
    return this.houses.length;
  }

  getBuiltCount(): number {
    return this.houses.filter(h => h.built).length;
  }

  getDevelopedCellCount(): number {
    let count = 0;
    for (const house of this.houses) {
      count += house.footprint.length;
    }
    return count;
  }

  /**
   * Get all houses.
   */
  getHouses(): readonly House[] {
    return this.houses;
  }

  /**
   * The complete state of every house, family and headline.
   *
   * Families in `Map` order rather than sorted: that order is the order they are
   * updated in, which makes it state rather than presentation. See
   * `core/snapshot.ts` on why a snapshot preserves order where a hash flattens it.
   *
   * The two timers are the reason this method exists and the client's old save
   * format was not enough. That format zeroed them, so a reloaded district began
   * its spawn and family cycles afresh — the hole `IMPLEMENTATION-GOVERNORS.md`
   * §5 item 6 names, and the one that made a reload a small gift of time.
   */
  save(): HouseSnapshot {
    return {
      houses: this.houses.map(cloneHouse),
      families: Array.from(this.families.values()).map(cloneFamily),
      nextHouseId: this.nextHouseId,
      nextFamilyId: this.nextFamilyId,
      spawnTimer: this.spawnTimer,
      familyUpdateTimer: this.familyUpdateTimer,
      pendingCarRequests: this.pendingCarRequests.map(r => ({ ...r })),
      news: this.news.map(n => ({ ...n })),
      rng: this.rng.save(),
    };
  }

  /** Restore from `save()`. */
  load(state: HouseSnapshot): void {
    this.houses = state.houses.map(cloneHouse);
    this.families.clear();
    for (const family of state.families) this.families.set(family.id, cloneFamily(family));
    this.nextHouseId = state.nextHouseId;
    this.nextFamilyId = state.nextFamilyId;
    this.spawnTimer = state.spawnTimer;
    this.familyUpdateTimer = state.familyUpdateTimer;
    this.pendingCarRequests = state.pendingCarRequests.map(r => ({ ...r }));
    this.news = state.news.map(n => ({ ...n }));
    this.rng.load(state.rng);
  }

  /**
   * Order-stable view of the built environment and the people in it.
   *
   * Houses keep array order, which is creation order and meaningful. Families
   * are sorted by id, since a restored world inserts them in a different
   * sequence than one that grew.
   */
  canonicalState(): Canonical {
    const houses = this.houses.map(h => ({
      id: h.id,
      x: h.gridX,
      y: h.gridY,
      cells: h.footprint.length,
      buildProgress: h.buildProgress,
      built: h.built,
      floors: h.floors,
      occupant: h.occupant,
      vacant: h.vacant,
      industry: h.industryType ?? null,
      revenue: h.revenue,
      taxesPaid: h.taxesPaid,
      dividendsPaid: h.dividendsPaid,
      familyShares: sortedShares(h.familyShares),
      corpShares: sortedShares(h.corpShares),
    }));

    const families = Array.from(this.families.values())
      .sort((a, b) => a.id - b.id)
      .map(f => {
        const record: Record<string, Canonical> = {
          id: f.id,
          name: f.name,
          home: f.homeHouseId,
          at: f.currentHouseId,
          state: f.state,
          target: f.targetHouseId,
          visitTimer: f.visitTimer,
          visitDuration: f.visitDuration,
          cash: f.cash,
          hardshipCycles: f.hardshipCycles,
          subscriptions: subscribedIndustries(f).map(i => `${i}:${f.subscriptions[i]}`),
          recurring: ALL_INDUSTRIES
            .filter(i => f.recurringPaymentTimers[i] !== undefined)
            .map(i => `${i}:${f.recurringPaymentTimers[i]}`),
          shares: sortedShares(f.shares),
        };
        // Written only when they are somewhere a house id cannot say. Absent for
        // every family who has never been on shift, which is what let a labour
        // market arrive without moving a hash recorded before there was one —
        // `canonicalize` cannot tell an absent key from an undefined one, and
        // that is exactly what the distinction is for.
        if (f.atFacilityId !== null) record.atFacility = f.atFacilityId;
        return record;
      });

    return {
      houses,
      families,
      nextHouseId: this.nextHouseId,
      nextFamilyId: this.nextFamilyId,
      spawnTimer: this.spawnTimer,
      familyUpdateTimer: this.familyUpdateTimer,
      // A leg is a house id, or `F` and a facility id. A trip between two
      // buildings therefore reads exactly as it always did, which is why every
      // recorded world's traffic hashes unchanged.
      pendingCarRequests: this.pendingCarRequests.map(
        r => `${r.familyId}:${tripLeg(r.pickupHouseId, r.pickupFacilityId)}` +
             `->${tripLeg(r.destinationHouseId, r.destinationFacilityId)}`
      ),
      // Stamps are sim-time now, so unlike the colony's they are part of the
      // hash: a headline that arrived on a different tick is a different world.
      news: this.news.map(n => `${Math.round(n.timestamp)}:${n.type}:${n.message}`),
      rng: this.rng.save() as unknown as Canonical,
    };
  }
}

/**
 * Deep copies for the snapshot path.
 *
 * A snapshot that shared its records with the live world would let a later tick
 * edit a document already written, which is the one thing a snapshot must not
 * do. Every nested object is copied on the way out and again on the way in.
 */
function cloneHouse(house: House): House {
  return {
    ...house,
    footprint: house.footprint.map(cell => ({ ...cell })),
    familyShares: { ...house.familyShares },
    corpShares: { ...house.corpShares },
  };
}

function cloneFamily(family: Family): Family {
  return {
    ...family,
    subscriptions: { ...family.subscriptions },
    recurringPaymentTimers: { ...family.recurringPaymentTimers },
    shares: { ...family.shares },
  };
}

/**
 * Shareholdings as `[corpId, count]` pairs in ascending id order.
 *
 * `Object.entries(family.shares)` gives the same order today, for a reason nothing
 * in the language promises: the keys are numeric strings, so V8 treats them as
 * array indices and returns them ascending. That accident is the one rule 4 was
 * written for — it is what paid dividends in a stable order before ADR-003 made
 * the ordering explicit. Here the order is the promise rather than the engine's.
 */
function shareEntries(shares: Record<number, number>): [number, number][] {
  return Object.keys(shares)
    .map(Number)
    .sort((a, b) => a - b)
    .map(id => [id, shares[id]]);
}

/** The same list, flattened for the hash. See `docs/determinism.md` rule 4. */
function sortedShares(shares: Record<number, number>): string[] {
  return shareEntries(shares).map(([id, count]) => `${id}:${count}`);
}

/** One end of a trip, for the hash: a house id, or `F` and a facility id. */
function tripLeg(houseId: number | null, facilityId: number | undefined): string {
  return facilityId === undefined ? `${houseId}` : `F${facilityId}`;
}
